import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, CheckIcon, Loader2Icon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button, LinkButton } from '../../components/ui/Button';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDateTime, telHref } from '../../utils/format';
import type { Enquiry } from '../../types/admin';
const ease = [0.23, 1, 0.32, 1] as const;
const statusStyles: Record<string, string> = {
  'Ny henvendelse': 'border-[#e6cfc2] bg-accent-soft text-accent-deep',
  Accepteret: 'border-ink/15 bg-ink text-white',
  Behandlet: 'border-line bg-canvas text-ink-soft',
  Afvist: 'border-danger/30 bg-danger/10 text-danger'
};
export function Enquiries() {
  const {
    enquiries,
    dataLoading,
    createCaseFromEnquiry,
    createQuoteFromEnquiry,
    acceptEnquiry,
    deleteEnquiry
  } = useAdminData();
  const navigate = useNavigate();
  const [working, setWorking] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Enquiry | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [actionError, setActionError] = useState('');
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-685" data-mp="3kwmdumzpz5jc">
      <p className="text-sm text-ink-muted" data-id="element-686" data-mp="3fxsz6r8p58hl">Fra formularen “Fortæl om din opgave”</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-687" data-mp="3twspe3buxvfv">
        Henvendelser
      </h1>

      {accepted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-688" data-mp="3pxtnkrlfaesn">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-689" data-mp="154ghxn6nskox">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-690" data-mp="1wf1h3hecrde" />
          </span>
          Henvendelsen er accepteret
        </p>}

      {actionError && <p role="alert" className="mt-5 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-691" data-mp="btjrjeed56zh">
          <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-692" data-mp="frr5k1hfzu6z" />
          {actionError}
        </p>}

      {deleted && <p role="status" className="mt-5 flex items-center gap-2 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink" data-id="element-693" data-mp="3k9d4jj9hk0bs">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-694" data-mp="3t0dh2chfwzmx">
            <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-695" data-mp="2wjitn78q8lu6" />
          </span>
          Henvendelsen er slettet
        </p>}

      {dataLoading && enquiries.length === 0 && <p className="mt-8 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-696" data-mp="2t1bn04tcjhbu">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-697" data-mp="2o1p87qxvdt0x" />
          Henter henvendelser …
        </p>}

      <ul className="mt-7 grid gap-5" data-id="element-698" data-mp="2jzc2v3n1bx4o">
        {enquiries.map(enquiry => <li key={enquiry.id} className="rounded-card border border-line bg-white p-5 md:p-6" data-id="element-699" data-mp="2f1ak6jtitztq">
            <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-700" data-mp="2a1nxljrsz789">
              <div data-id="element-701" data-mp="2a3sc0vnbvoms">
                <p className="text-xs text-ink-muted" data-id="element-702" data-mp="25bha5n911srl">{formatDateTime(enquiry.createdAt) || 'Netop nu'}</p>
                <h2 className="mt-1 font-display text-xl font-bold tracking-tight text-ink" data-id="element-703" data-mp="20bmllqcq5f9p">{enquiry.name}</h2>
              </div>
              <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[enquiry.status] ?? 'border-line bg-canvas text-ink-soft'}`} data-id="element-704" data-mp="1wbu60towy8uu">
                {enquiry.status}
              </span>
            </div>

            <dl className="mt-4 grid gap-3 text-[15px] sm:grid-cols-3" data-id="element-705" data-mp="3mfxg259lrfbk">
              <div data-id="element-706" data-mp="3re7gove3j84k">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-707" data-mp="3rce5r3wga1qr">Telefon</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-708" data-mp="3cnrt59y7jo38">
                  <PhoneIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-709" data-mp="3hnmfr1ay9rih" />
                  {enquiry.phone || '—'}
                </dd>
              </div>
              <div data-id="element-710" data-mp="33q7qpxolzlr1">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-711" data-mp="37pq9onwvdmr8">E-mail</dt>
                <dd className="mt-1 flex items-center gap-1.5 break-all text-ink" data-id="element-712" data-mp="37ndl3rwlwe57">
                  <MailIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden="true" data-id="element-713" data-mp="2ts1pliammjn8" />
                  {enquiry.email || '—'}
                </dd>
              </div>
              <div data-id="element-714" data-mp="rzvje7sgyegf">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-715" data-mp="rwhiz53d8fbm">Adresse / område</dt>
                <dd className="mt-1 flex items-center gap-1.5 text-ink" data-id="element-716" data-mp="wvkosrr66pkn">
                  <MapPinIcon className="h-4 w-4 text-ink-faint" aria-hidden="true" data-id="element-717" data-mp="2hghfwl3gwx53" />
                  {enquiry.address || '—'}
                </dd>
              </div>
            </dl>

            <p className="mt-4 whitespace-pre-line border-t border-line pt-4 text-[15px] leading-relaxed text-ink-soft" data-id="element-718" data-mp="2r6kgb6cx5ddg">
              {enquiry.description}
            </p>

            {enquiry.images.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-719" data-mp="304mele59prx1">
                {enquiry.images.map(image => <li key={image.path} data-id="element-720" data-mp="2v9gsofbv8d8y">
                    <a href={image.url} target="_blank" rel="noreferrer" data-id="element-721" data-mp="3a4mzv5mp6i3v">
                      <img src={image.url} alt={image.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-722" data-mp="355abuhk3b4zp" />
                    </a>
                  </li>)}
              </ul>}

            <div className="mt-5 flex flex-wrap gap-3" data-id="element-723" data-mp="20foem6olrldt">
              {enquiry.status === 'Ny henvendelse' && <Button size="sm" disabled={accepting === enquiry.id} onClick={async () => {
            if (accepting) return;
            setAccepting(enquiry.id);
            setActionError('');
            setDeleted(false);
            const result = await acceptEnquiry(enquiry.id);
            setAccepting(null);
            if (!result.ok) {
              setActionError(result.error ?? 'Henvendelsen kunne ikke accepteres. Prøv igen.');
              return;
            }
            setAccepted(true);
          }} data-id="element-724" data-mp="24g89f3lpecws">
                  {accepting === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-725" data-mp="322xh9u337jqk" />
                      Accepterer …
                    </> : 'Accepter henvendelse'}
                </Button>}

              <Button variant="secondary" size="sm" disabled={working === `quote-${enquiry.id}`} onClick={async () => {
            setWorking(`quote-${enquiry.id}`);
            setActionError('');
            const created = await createQuoteFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Tilbuddet kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/tilbud/${created.id}`);
          }} data-id="element-726" data-mp="qg3ps97ep261">
                {working === `quote-${enquiry.id}` ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-727" data-mp="1wzk4860yx68n" />
                    Opretter …
                  </> : 'Opret tilbud'}
              </Button>

              {enquiry.caseId ? <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/sager/${enquiry.caseId}`)} data-id="element-728" data-mp="15apgwpt4y3ke">
                  Åbn sagen
                </Button> : <Button variant={enquiry.status === 'Ny henvendelse' ? 'secondary' : 'primary'} size="sm" disabled={working === enquiry.id} onClick={async () => {
            setWorking(enquiry.id);
            setActionError('');
            const created = await createCaseFromEnquiry(enquiry);
            setWorking(null);
            if (!created) {
              setActionError('Sagen kunne ikke oprettes. Prøv igen.');
              return;
            }
            navigate(`/admin/sager/${created.id}`);
          }} data-id="element-729" data-mp="winiex2mmxcr">
                  {working === enquiry.id ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-730" data-mp="3nfp6d4gd8sdr" />
                      Opretter …
                    </> : 'Opret sag'}
                </Button>}
              {enquiry.phone && <LinkButton href={telHref(enquiry.phone)} variant="secondary" size="sm" data-id="element-731" data-mp="38qssurvjin5r">
                  Ring
                </LinkButton>}
              {enquiry.email && <LinkButton href={`mailto:${enquiry.email}`} variant="secondary" size="sm" data-id="element-732" data-mp="34neavvo1jwpo">
                  Send e-mail
                </LinkButton>}
              <button type="button" onClick={() => {
            setConfirming(enquiry);
            setDeleteError('');
            setDeleted(false);
            setAccepted(false);
            setActionError('');
          }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-danger/40 hover:text-danger" data-id="element-733" data-mp="3docienxoumd0">
                Slet henvendelse
              </button>
            </div>
          </li>)}
      </ul>

      {confirming && <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5" role="dialog" aria-modal="true" aria-labelledby="slet-titel" data-id="element-734" data-mp="5255h31z713l">
          <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.2,
        ease
      }} className="w-full max-w-md rounded-card bg-white p-6" data-id="element-735" data-mp="3rhgcq2t5xzwn">
            <h2 id="slet-titel" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-736" data-mp="3igi6ddv95gaw">
              Er du sikker på, at du vil slette denne henvendelse?
            </h2>
            <p className="mt-3 text-[15px] text-ink-soft" data-id="element-737" data-mp="1syuskecn9ew2">
              Henvendelsen fra {confirming.name} og dens billeder slettes permanent.
            </p>

            {deleteError && <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger" data-id="element-738" data-mp="221nj2fe05mku">
                <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-739" data-mp="1a9ohndi3drkt" />
                {deleteError}
              </p>}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse" data-id="element-740" data-mp="1ebjdi2h97cr7">
              <Button variant="dark" disabled={deleting} onClick={async () => {
            if (deleting) return;
            setDeleting(true);
            setDeleteError('');
            const result = await deleteEnquiry(confirming);
            setDeleting(false);
            if (!result.ok) {
              setDeleteError(result.error ?? 'Henvendelsen kunne ikke slettes. Prøv igen.');
              return;
            }
            setConfirming(null);
            setDeleted(true);
          }} data-id="element-741" data-mp="1j91r4667ug5j">
                {deleting ? <>
                    <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-742" data-mp="23lw8bc4mjuwm" />
                    Sletter …
                  </> : 'Slet henvendelse'}
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirming(null)} data-id="element-743" data-mp="1az1rqqaxhfof">
                Annuller
              </Button>
            </div>
          </motion.div>
        </div>}

      {!dataLoading && enquiries.length === 0 && <p className="mt-8 rounded-card border border-dashed border-line bg-white p-10 text-center text-sm text-ink-muted" data-id="element-744" data-mp="30bvpfuyuc1qt">
          Der er ingen henvendelser endnu.
        </p>}
    </div>;
}